import React, { useState } from 'react';

import useRect from '@lyearn/core/hooks/useRect';
import { ObservedSize } from '@lyearn/core/hooks/useRect/types';
import { cn } from '@lyearn/style';
import { Box, Stack } from '@lyearn/ui';
import Loader from '@/components/Loader';
import VirtualizedList from '@/components/VirtualizedList';

import { rowContainerStyles } from './styles';
import { GridProps } from './types';

const CARD_WIDTH = 276;

function Grid(props: GridProps) {
  const {
    fetching,
    reFetching,
    itemsCount = 0,
    itemSpacing = 20,
    itemRenderer,
    fetchMore,
    className,
    itemWidth = CARD_WIDTH,
    rowClassName = 'py-10',
    atTopStateChange,
    useWindowScroll = true,
    virtualizedListClassName,
    scrollElement,
  } = props;

  const [rect, setRect] = useState<ObservedSize>({
    width: undefined,
    height: undefined,
  });

  // TODO: use ref instead of onResize @harshitdavda @iamsaumya
  const { ref } = useRect<HTMLDivElement>({
    onResize: setRect,
  });

  if (fetching) {
    return <Loader />;
  }

  const itemsPerRow = Math.floor((rect?.width ?? 992) / itemWidth);
  const rowCount = Math.ceil(itemsCount / itemsPerRow);

  return (
    <Stack ref={ref} className={className} direction="col" justifyContent="end">
      <VirtualizedList
        atTopStateChange={atTopStateChange}
        className={virtualizedListClassName}
        customScrollParent={scrollElement}
        endReached={fetchMore}
        itemContent={(index) => {
          const rowItems = [];
          const fromIndex = index * itemsPerRow;
          const toIndex = Math.min(fromIndex + itemsPerRow, itemsCount);

          for (let i = fromIndex; i < toIndex; i++) {
            rowItems.push(itemRenderer({ index: i }));
          }

          if (index === rowCount - 1) {
            for (let i = toIndex; i < rowCount * itemsPerRow; i++) {
              rowItems.push(<Stack className="flex-1 opacity-0" />);
            }
          } // to align items in last row within grid blocks when count is less than itemsPerRow; to be removed later after grid component update

          return (
            <Stack className={cn(rowClassName, rowContainerStyles({ itemsPerRow, itemSpacing }))}>
              {rowItems}
            </Stack>
          );
        }}
        totalCount={rowCount}
        useWindowScroll={useWindowScroll}
      />
      {reFetching ? (
        <Box className="my-32 mb-96 flex w-full">
          <Loader className="mx-auto mb-96 self-center" showLabel={false} />
        </Box>
      ) : null}
    </Stack>
  );
}

export default Grid;
